import { useState } from 'react'
import { Group, Pagination, SegmentedControl, Stack, Table, Text } from '@mantine/core'
import { NOT_MEASURED, exactNumber } from '../../utils/format'
import { VerdictBadge } from './VerdictBadge'
import {
  concurrencyLabel, countLabel, dayLabel, durationLabel, floorMark, isBusyBucket, isFloorBucket, offsetLabel,
  ratioPercent, sameInstant, slotSecondsLabel, verdictLabel, wallClock,
} from './scheduleFormat'
import type { ScheduleLoadBucket } from '../../types/scheduleLoad'

/** Twelve hours of quarter-hours a page — a working day reads as one or two pages. */
const PAGE_SIZE = 48

/**
 * The quarter-hours, as numbers — the evidence behind the timeline's colours.
 *
 * Every figure a verdict rests on is a column here, not a tooltip (S3): what
 * starts, how much of it is unestimated, what it wants, and what the pool can
 * give. The unknown count sits beside the start count in every row, so "200
 * starting, 200 with no estimate" can never be read as "200 starting".
 *
 * Opens on the quarter-hours where something starts or waits, and says how many
 * it leaves out. The rest start nothing and nothing waits in them; they are all
 * on the timeline, and one control away here. Paging is over the fully fetched
 * forecast — nothing is filtered server-side, so the counts above the table are
 * totals, not a window's worth.
 */
export function QuarterHourTable({
  buckets, selectedUtc, onSelect,
}: {
  buckets: ScheduleLoadBucket[]
  selectedUtc: string | null
  onSelect: (startUtc: string) => void
}) {
  const [scope, setScope] = useState<'busy' | 'all'>('busy')
  const [page, setPage] = useState(1)

  const busy = buckets.filter(isBusyBucket)
  const rows = scope === 'busy' ? busy : buckets
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const current = Math.min(page, totalPages)
  const visible = rows.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE)

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Text size="sm" c="dimmed">
          {scope === 'busy'
            ? `${exactNumber(busy.length)} of ${exactNumber(buckets.length)} quarter-hours have something starting or `
              + 'waiting. The others start nothing and nothing waits in them; their verdicts are on the timeline.'
            : `All ${exactNumber(buckets.length)} quarter-hours in the window.`}
        </Text>
        <SegmentedControl
          size="xs"
          value={scope}
          onChange={(value) => {
            setScope(value === 'all' ? 'all' : 'busy')
            setPage(1)
          }}
          data={[
            { value: 'busy', label: 'Starting or waiting' },
            { value: 'all', label: 'Every quarter-hour' },
          ]}
        />
      </Group>

      {rows.length === 0 ? (
        <Text size="sm" c="dimmed">
          Nothing is scheduled to start in this window and nothing waits for a slot. That is a finding about the
          scheduled demand this page can see — the verdict block above lists what it cannot.
        </Text>
      ) : (
        <>
          <Table.ScrollContainer minWidth={1180}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Quarter-hour</Table.Th>
                  <Table.Th>Verdict</Table.Th>
                  <Table.Th ta="right">Starting</Table.Th>
                  <Table.Th ta="right">No estimate</Table.Th>
                  <Table.Th ta="right">Floor cost</Table.Th>
                  <Table.Th ta="right">Unknown still running</Table.Th>
                  <Table.Th ta="right">Wanted (planning)</Table.Th>
                  <Table.Th ta="right">Avg slots wanted</Table.Th>
                  <Table.Th ta="right">Most at once</Table.Th>
                  <Table.Th ta="right">Utilization</Table.Th>
                  <Table.Th ta="right">Longest queue</Table.Th>
                  <Table.Th ta="right">Longest wait, of those starting here</Table.Th>
                  <Table.Th ta="right">Waiting at end</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visible.map((bucket) => (
                  <BucketRow
                    key={bucket.startUtc}
                    bucket={bucket}
                    selected={sameInstant(bucket.startUtc, selectedUtc)}
                    onSelect={onSelect}
                  />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Text size="xs" c="dimmed">
            <strong>&ge; marks a floor.</strong> In that quarter-hour something has no estimate, a floor cost, a
            cut-short series or an hour the page cannot compute, and it adds no slot time to the demand and
            occupancy figures — so a row of 200 unknowns can read &ldquo;0 slot-s&rdquo; while being anything but
            idle. A dash is unmeasured: there is no capacity to judge against.
          </Text>
          {totalPages > 1 && (
            <Group justify="space-between">
              <Text size="sm" c="dimmed">Page {current} of {totalPages}</Text>
              <Pagination value={current} total={totalPages} onChange={setPage} />
            </Group>
          )}
        </>
      )}
    </Stack>
  )
}

function BucketRow({
  bucket, selected, onSelect,
}: {
  bucket: ScheduleLoadBucket
  selected: boolean
  onSelect: (startUtc: string) => void
}) {
  const wall = wallClock(bucket.start)
  const floor = isFloorBucket(bucket)
  const numeric ={ fontVariantNumeric: 'tabular-nums' as const, whiteSpace: 'nowrap' as const }

  return (
    <Table.Tr
      style={{
        cursor: 'pointer',
        outline: selected ? '2px solid var(--color-focus-ring)' : undefined,
        outlineOffset: -2,
      }}
      onClick={() => onSelect(bucket.startUtc)}
      aria-selected={selected}
    >
      <Table.Td style={{ whiteSpace: 'nowrap' }}>
        {/*
          A real button in the first cell, so the row is reachable by keyboard —
          a <tr> takes no focus, and a click-only row is a keyboard dead end.
        */}
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation()
            onSelect(bucket.startUtc)
          }}
          style={{
            all: 'unset', cursor: 'pointer', display: 'flex', flexDirection: 'column',
          }}
          aria-label={`Open the quarter-hour from ${wall?.time ?? bucket.startUtc}`}
        >
          <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>{wall?.time ?? NOT_MEASURED}</Text>
          <Text size="xs" c="dimmed">
            {wall ? `${dayLabel(wall.date)} · ${offsetLabel(wall.offsetMinutes)}` : 'unreadable time'}
          </Text>
        </button>
      </Table.Td>
      <Table.Td>
        <Stack gap={2}>
          <VerdictBadge verdict={bucket.verdict} />
          {bucket.verdict === 'Indeterminate' && bucket.floorVerdict != null && (
            <Text size="xs" c="dimmed">known part alone: {verdictLabel(bucket.floorVerdict)}</Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td ta="right" style={numeric}>{exactNumber(bucket.starts)}</Table.Td>
      <Table.Td ta="right" style={numeric}>
        <Text span size="sm" c={bucket.unestimatedStarts > 0 ? 'orange' : undefined}>
          {exactNumber(bucket.unestimatedStarts)}
        </Text>
      </Table.Td>
      <Table.Td ta="right" style={numeric}>{exactNumber(bucket.floorCostStarts)}</Table.Td>
      <Table.Td ta="right" style={numeric}>{exactNumber(bucket.unknownPossiblyRunning)}</Table.Td>
      <Table.Td ta="right" style={numeric}>{floorMark(slotSecondsLabel(bucket.planningDemandSlotSeconds), floor)}</Table.Td>
      <Table.Td ta="right" style={numeric}>{floorMark(concurrencyLabel(bucket.expectedConcurrency), floor)}</Table.Td>
      <Table.Td ta="right" style={numeric}>{exactNumber(bucket.peakDemandConcurrency)}</Table.Td>
      {/* Not marked at 100%: occupancy cannot exceed the pool, so a full pool is exact. */}
      <Table.Td ta="right" style={numeric}>
        {floorMark(ratioPercent(bucket.utilization), floor && (bucket.utilization ?? 0) < 1)}
      </Table.Td>
      <Table.Td ta="right" style={numeric}>{countLabel(bucket.peakQueueLength)}</Table.Td>
      <Table.Td ta="right" style={numeric}>{durationLabel(bucket.maxWaitSeconds)}</Table.Td>
      <Table.Td ta="right" style={numeric}>{countLabel(bucket.backlogAtEnd)}</Table.Td>
    </Table.Tr>
  )
}
